import {
    AuthContext,
    Badge,
    BadgeType,
    ProviderExperiment,
    ProviderEvidence,
    ProviderMissingEvidence,
    ProviderExperimentClaim,
    ToastContext,
    Tooltip,
} from '@enter/core';
import { PageLayout, PageHeader, PageLayoutRow } from 'components';
import { inject, observer } from 'mobx-react';
import React, { useCallback, useContext, useEffect, useState, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { DialogContext } from 'services';
import { IInjectedRootStore } from 'stores';
import { format, differenceInDays, differenceInHours } from 'date-fns';
import { 
    mdiContentCopy, 
    mdiShare,
    mdiHistory, 
    mdiUpdate, 
    mdiCheckCircleOutline, 
    mdiBlockHelper, 
    mdiAlertCircleOutline 
} from '@mdi/js';
import Icon from '@mdi/react';
import { RequiredEvidenceTable } from './components/required-evidence-table';
import { MissingEvidenceTable } from './components/missing-evidence-table';
import { ClaimsTable } from './components/claims-table';
import { ExperimentInfoCards } from './components/experiment-info-cards';
import { EvidenceViewDialog } from '../../evidence/evidence-view-dialog';
import * as S from './experiment-details.styles';

const getExperimentStatusBadgeType = (status: string | undefined): BadgeType => {
    switch (status) {
        case "NOT_STARTED":
            return BadgeType.BLUE;
        case "IN_PROGRESS":
            return BadgeType.ORANGE;
        case "COMPLETED":
            return BadgeType.GREEN;
        case "CANCELED":
            return BadgeType.BLUE;
        case "FAILING":
            return BadgeType.RED;
        default:
            return BadgeType.BLUE;
    }
};

const formatRunTime = (created: string | undefined): string => {
    if (!created) return 'N/A';
    const now = new Date();
    const createdDate = new Date(created);
    const diffDays = differenceInDays(now, createdDate);
    
    if (diffDays < 1) {
        const diffHours = differenceInHours(now, createdDate);
        const hourLabel = diffHours === 1 ? ' hour' : ' hours';
        return diffHours + hourLabel;
    } else {
        const dayLabel = diffDays === 1 ? ' day' : ' days';
        return diffDays + dayLabel;
    }
};

const formatDateRange = (fromDate: string | undefined, throughDate: string | undefined): string => {
    if (!fromDate || !throughDate) return 'N/A';
    const from = format(new Date(fromDate), "MMM d, yyyy");
    const through = format(new Date(throughDate), "MMM d, yyyy");
    return `${from} – ${through}`;
};

type IInjectedProps = IInjectedRootStore;

export const ExperimentDetails = inject('rootStore')(
    observer((props: {}) => {
        const { rootStore } = props as IInjectedProps;
        const { experimentId } = useParams<{ experimentId: string }>();
        const navigate = useNavigate();
        
        if (!rootStore || !experimentId) {
            return <></>;
        }

        const authContext = useContext(AuthContext);
        const dialogContext = useContext(DialogContext);
        const toastContext = useContext(ToastContext);
        
        const [experiment, setExperiment] = useState<ProviderExperiment | undefined>();
        const [evidence, setEvidence] = useState<ProviderEvidence[]>([]);
        const [missingEvidence, setMissingEvidence] = useState<ProviderMissingEvidence[]>([]);
        const [claims, setClaims] = useState<ProviderExperimentClaim[]>([]);
        const [loading, setLoading] = useState(false);
        const [evidenceLoading, setEvidenceLoading] = useState(false);
        const [missingEvidenceLoading, setMissingEvidenceLoading] = useState(false);
        const [claimsLoading, setClaimsLoading] = useState(false);
        const [claimsFilter, setClaimsFilter] = useState<string>('Test Claims');
        const fetchedExperimentIdRef = useRef<string | null>(null);
        const fetchedClaimsKeyRef = useRef<string | null>(null);
        const isFetchingExperimentRef = useRef<boolean>(false);
        const isFetchingEvidenceRef = useRef<boolean>(false);
        const isFetchingMissingEvidenceRef = useRef<boolean>(false);
        const isFetchingClaimsRef = useRef<boolean>(false);

        const handleFetchExperiment = useCallback(async () => {
            if (!experimentId) return;
            // Prevent concurrent fetches
            if (isFetchingExperimentRef.current) return;
            isFetchingExperimentRef.current = true;
            setLoading(true);
            try {
                authContext.setGroup?.('fetchExperiment');
                const fetchedExperiment = await rootStore.experimentsStore.getExperiment(experimentId);
                setExperiment(fetchedExperiment);
                fetchedExperimentIdRef.current = experimentId;
            } catch (error) {
                console.error('Error fetching experiment:', error);
                dialogContext.showError?.(error);
                // Keep the ref set to prevent infinite retry loop
                fetchedExperimentIdRef.current = experimentId;
            } finally {
                setLoading(false);
                isFetchingExperimentRef.current = false;
            }
        }, [experimentId, rootStore, authContext, dialogContext]);

        const handleFetchEvidence = useCallback(async () => {
            if (!experimentId) return;
            // Prevent concurrent fetches
            if (isFetchingEvidenceRef.current) return;
            isFetchingEvidenceRef.current = true;
            setEvidenceLoading(true);
            try {
                authContext.setGroup?.('fetchEvidence');
                const listing = await rootStore.evidenceStore.loadEvidenceList({
                    page: 0,
                    pageSize: 100,
                    filterExperimentId: [experimentId],
                });
                setEvidence(listing.evidence);
            } catch (error) {
                console.error('Error fetching evidence:', error);
                // Error is logged but doesn't prevent retry on experimentId change
            } finally {
                setEvidenceLoading(false);
                isFetchingEvidenceRef.current = false;
            }
        }, [experimentId, rootStore, authContext]);

        const handleFetchMissingEvidence = useCallback(async () => {
            if (!experimentId) return;
            // Prevent concurrent fetches
            if (isFetchingMissingEvidenceRef.current) return;
            isFetchingMissingEvidenceRef.current = true;
            setMissingEvidenceLoading(true);
            try {
                authContext.setGroup?.('fetchMissingEvidence');
                const listing = await rootStore.evidenceStore.loadMissingEvidenceList({
                    page: 0,
                    pageSize: 100,
                    filterExperimentId: [experimentId],
                });
                setMissingEvidence(listing.missingEvidence);
            } catch (error) {
                console.error('Error fetching missing evidence:', error);
                // Error is logged but doesn't prevent retry on experimentId change
            } finally {
                setMissingEvidenceLoading(false);
                isFetchingMissingEvidenceRef.current = false;
            }
        }, [experimentId, rootStore, authContext]);

        const handleFetchClaims = useCallback(async () => {
            if (!experimentId) return;
            const claimsKey = `${experimentId}-${claimsFilter}`;
            if (fetchedClaimsKeyRef.current === claimsKey) return; // Already fetched for this key
            // Prevent concurrent fetches
            if (isFetchingClaimsRef.current) return;
            isFetchingClaimsRef.current = true;
            
            setClaimsLoading(true);
            try {
                authContext.setGroup?.('fetchClaims');
                const claimsData = await rootStore.experimentsStore.getExperimentClaims(
                    experimentId,
                    claimsFilter === 'Test Claims'
                );
                setClaims(claimsData);
                fetchedClaimsKeyRef.current = claimsKey;
            } catch (error) {
                console.error('Error fetching claims:', error);
                toastContext.notify?.('Failed to fetch claims', 'right');
                // Keep the ref set to prevent infinite retry loop
                fetchedClaimsKeyRef.current = claimsKey;
            } finally {
                setClaimsLoading(false);
                isFetchingClaimsRef.current = false;
            }
        }, [experimentId, claimsFilter, rootStore, authContext, toastContext]);

        // Update refs when callbacks change (for use in useEffect)
        const handleFetchExperimentRef = useRef(handleFetchExperiment);
        const handleFetchEvidenceRef = useRef(handleFetchEvidence);
        const handleFetchMissingEvidenceRef = useRef(handleFetchMissingEvidence);
        const handleFetchClaimsRef = useRef(handleFetchClaims);
        
        handleFetchExperimentRef.current = handleFetchExperiment;
        handleFetchEvidenceRef.current = handleFetchEvidence;
        handleFetchMissingEvidenceRef.current = handleFetchMissingEvidence;
        handleFetchClaimsRef.current = handleFetchClaims;

        const handleClaimsFilterChange = useCallback((filterValue: string) => {
            setClaimsFilter(filterValue);
        }, []);

        const handleGoToClaim = useCallback((claimId: string) => {
            navigate(`/claims/${claimId}`);
        }, [navigate]);

        const handleViewEvidence = useCallback((evidence: ProviderEvidence): void => {
            dialogContext.showDialog?.({
                props: {
                    onClose: (): void => {
                        dialogContext.hideDialog?.();
                    },
                    disableBackdropClick: false,
                    disableEscapeKeyDown: false,
                    renderTitle: 'View Evidence',
                },
                Component: <EvidenceViewDialog evidence={evidence} onClose={(): void => dialogContext.hideDialog?.()} />,
            });
        }, [dialogContext]);

        useEffect(() => {
            // Only fetch if experimentId changed and we haven't fetched it yet
            if (experimentId && fetchedExperimentIdRef.current !== experimentId) {
                handleFetchExperimentRef.current();
                handleFetchEvidenceRef.current();
                handleFetchMissingEvidenceRef.current();
            }
            
            // Cleanup: cancel groups when component unmounts or experimentId changes
            return () => {
                authContext.cancelGroup?.('fetchExperiment');
                authContext.cancelGroup?.('fetchEvidence');
                authContext.cancelGroup?.('fetchMissingEvidence');
            };
        }, [experimentId, authContext]);

        useEffect(() => {
            // Only fetch if experimentId or claimsFilter changed
            if (experimentId) {
                const claimsKey = `${experimentId}-${claimsFilter}`;
                if (fetchedClaimsKeyRef.current !== claimsKey) {
                    handleFetchClaimsRef.current();
                }
            }
            
            // Cleanup: cancel group when component unmounts or dependencies change
            return () => {
                authContext.cancelGroup?.('fetchClaims');
            };
        }, [experimentId, claimsFilter, authContext]);

        useEffect(() => {
            document.title = experiment ? `Experiment Details - ${experiment.id}` : 'Experiment Details';
        }, [experiment]);

        const copyToClipboard = (text: string, successMessage: string) => {
            navigator.clipboard.writeText(text).then(() => {
                toastContext.notify?.(successMessage, 'right');
            }).catch(err => {
                console.error('Failed to copy: ', err);
                toastContext.notify?.('Failed to copy to clipboard', 'right');
            });
        };

        const copyExperimentId = () => {
            if (experimentId) {
                copyToClipboard(experimentId, 'Experiment ID copied to clipboard');
            }
        };

        const copyShareUrl = () => {
            const currentUrl = window.location.href;
            copyToClipboard(currentUrl, 'URL copied to clipboard');
        };

        if (loading) {
            return (
                <PageLayout>
                    <PageHeader title="Experiment Details" hideDateRange={true} />
                    <PageLayoutRow>
                        <S.LoadingMessage>Loading experiment details...</S.LoadingMessage>
                    </PageLayoutRow>
                </PageLayout>
            );
        }

        if (!experiment) {
            return (
                <PageLayout>
                    <PageHeader title="Experiment Details" hideDateRange={true} />
                    <PageLayoutRow>
                        <S.ErrorMessage>Experiment not found</S.ErrorMessage>
                    </PageLayoutRow>
                </PageLayout>
            );
        }

        return (
            <PageLayout>
                <PageHeader title="Experiment Details" hideDateRange={true} />
                <PageLayoutRow>
                    <S.HeaderRow>
                        {/* Status and Experiment Information as Badges */}
                        <S.BadgesContainer>
                            {/* Status Badge */}
                            <Tooltip
                                direction="top"
                                variant="dark"
                                title={<span>Experiment Status: {experiment.status}</span>}>
                                <Badge type={getExperimentStatusBadgeType(experiment.status)} size="small">
                                    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                                        {experiment.status === 'NOT_STARTED' && (
                                            <Icon path={mdiHistory} size={0.7} />
                                        )}
                                        {experiment.status === 'IN_PROGRESS' && (
                                            <Icon path={mdiUpdate} size={0.7} />
                                        )}
                                        {experiment.status === 'COMPLETED' && (
                                            <Icon path={mdiCheckCircleOutline} size={0.7} />
                                        )}
                                        {experiment.status === 'CANCELED' && (
                                            <Icon path={mdiBlockHelper} size={0.7} />
                                        )}
                                        {experiment.status === 'FAILING' && (
                                            <Icon path={mdiAlertCircleOutline} size={0.7} />
                                        )}
                                        <span>{experiment.status || 'N/A'}</span>
                                    </span>
                                </Badge>
                            </Tooltip>

                            {/* DOS Range Badge */}
                            <Tooltip
                                direction="top"
                                variant="dark"
                                title={<span>Date of Service Range</span>}>
                                <Badge type={BadgeType.BLUE_LIGHT} size="small">
                                    DOS: {formatDateRange(experiment.fromDateOfService, experiment.throughDateOfService)}
                                </Badge>
                            </Tooltip>

                            {/* Run Time Badge */}
                            <Tooltip
                                direction="top"
                                variant="dark"
                                title={<span>Experiment Run Time</span>}>
                                <Badge type={BadgeType.BLUE_LIGHT} size="small">
                                    Run Time: {formatRunTime(experiment.created)}
                                </Badge>
                            </Tooltip>
                        </S.BadgesContainer>

                        {/* Header Actions */}
                        <S.HeaderActions>
                            {/* Copy Experiment ID Button */}
                            <Tooltip
                                direction="top"
                                variant="dark"
                                title={<span>Copy Experiment ID</span>}>
                                <S.ActionButton onClick={copyExperimentId}>
                                    <Icon path={mdiContentCopy} size={0.8} />
                                </S.ActionButton>
                            </Tooltip>

                            {/* Share URL Button */}
                            <Tooltip
                                direction="top"
                                variant="dark"
                                title={<span>Share URL</span>}>
                                <S.ActionButton onClick={copyShareUrl}>
                                    <Icon path={mdiShare} size={0.8} />
                                </S.ActionButton>
                            </Tooltip>
                        </S.HeaderActions>
                    </S.HeaderRow>
                </PageLayoutRow>

                <PageLayoutRow>
                    <S.Content>
                        {/* Providers and Payer/Facility Containers */}
                        <ExperimentInfoCards experiment={experiment} dialogContext={dialogContext} />

                        {/* Required Evidence Table */}
                        <S.TableSection>
                            <RequiredEvidenceTable
                                data={evidence}
                                isLoading={evidenceLoading}
                                onViewEvidence={handleViewEvidence}
                            />
                        </S.TableSection>

                        {/* Missing Evidence Table */}
                        <S.TableSection>
                            <MissingEvidenceTable
                                data={missingEvidence}
                                isLoading={missingEvidenceLoading}
                            />
                        </S.TableSection>

                        {/* Claims Table */}
                        <S.TableSection>
                            <ClaimsTable
                                data={claims}
                                isLoading={claimsLoading}
                                onGoToClaim={handleGoToClaim}
                                onFilterChange={handleClaimsFilterChange}
                            />
                        </S.TableSection>
                    </S.Content>
                </PageLayoutRow>
            </PageLayout>
        );
    }),
);
